import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";

export default function StripeCheckoutForm() {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const items = useSelector(state => state.cart.items);
  const grandTotal = items?.reduce((amount, item) => item.price * item.quantity + amount, 0);

  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [isStripeLoaded, setIsStripeLoaded] = useState(false);

  useEffect(() => {
    if (!stripe) return;

    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    );

    if (!clientSecret) return;

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent.status) {
        case "succeeded":
          setMessage("Payment succeeded!");
          break;
        case "processing":
          setMessage("Your payment is processing.");
          break;
        case "requires_payment_method":
          setMessage("Your payment was not successful, please try again.");
          break;
        default:
          setMessage("Something went wrong.");
          break;
      }
    });
  }, [stripe]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsLoading(true);
    setMessage(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (error) {
      if (error.type === "card_error" || error.type === "validation_error") {
        setMessage(error.message);
      } else {
        setMessage("An unexpected error occurred.");
      }
      setIsLoading(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      navigate(`/order-process?payment_intent=${paymentIntent.id}&redirect_status=succeeded`);
    }
  };

  const paymentElementOptions = {
    layout: "tabs",
  }

  return (
    <form id="payment-form" onSubmit={handleSubmit} className="space-y-6">
      {!isStripeLoaded && (
        <div className="flex flex-col space-y-4 animate-pulse">
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      )}

      {/* Actual Stripe Element - initially hidden or visible based on loading */}
      <div className={!isStripeLoaded ? "opacity-0 h-0" : "opacity-100 transition-opacity duration-500"}>
        <PaymentElement
          id="payment-element"
          options={paymentElementOptions}
          onReady={() => setIsStripeLoaded(true)}
        />
      </div>

      <button
        disabled={isLoading || !stripe || !elements || !isStripeLoaded}
        id="submit"
        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        <span id="button-text">
          {isLoading ? <div className="spinner" id="spinner"></div> : `Pay ₹${grandTotal?.toLocaleString()}`}
        </span>
      </button>

      {message && (
        <div id="payment-message" className="p-3 rounded-md bg-red-50 text-red-600 text-sm font-medium">
          {message}
        </div>
      )}
    </form>
  );
}